import { coordKey, getNeighbors } from "../../hex-path/hex-grid";

export interface HexCoord {
	row: number;
	col: number;
}

/** Matches `--sq-hex-tile-duration-out` in hexagon-tile-surfaces.css. */
export const HEX_PATH_TILE_TRAIL_SCALE_OUT_MS = 600;

export interface HexGridHoverDomOptions {
	hoverScale: number;
	activeCharacterKey: string | null;
	/** Path tiles in this set skip hover scale and path-tile hover styling. */
	lockedPathCoordKeys?: ReadonlySet<string>;
}

function resolveTileRoot(cellRoot: HTMLElement): HTMLElement | null {
	return cellRoot.querySelector<HTMLElement>(".sq-hex-grid-tile");
}

function resolvePathTileRoot(cellRoot: HTMLElement): HTMLElement | null {
	return cellRoot.querySelector<HTMLElement>(".sq-hex-path-tile");
}

function isTrailCell(
	cellKey: string,
	hovered: HexCoord | null,
	hoveredKey: string | null,
): boolean {
	if (!hovered || !hoveredKey || cellKey === hoveredKey) {
		return false;
	}
	return getNeighbors(hovered.row, hovered.col).some(
		(neighbor) => coordKey(neighbor.row, neighbor.col) === cellKey,
	);
}

function syncHexCellVisuals(
	cellRoot: HTMLElement,
	cellKey: string,
	hovered: HexCoord | null,
	options: HexGridHoverDomOptions,
): void {
	const hoveredKey = hovered ? coordKey(hovered.row, hovered.col) : null;
	const isLockedPathTile = options.lockedPathCoordKeys?.has(cellKey) ?? false;
	const isHovered = cellKey === hoveredKey;
	const isCharacter = cellKey === options.activeCharacterKey;
	const isTrail = isTrailCell(cellKey, hovered, hoveredKey);
	const isHoverScaled = isHovered && !isLockedPathTile;
	const isPathTrail = isTrail && !isLockedPathTile;
	const isCharacterScaled = isCharacter;
	const scale = isHoverScaled || isCharacterScaled ? options.hoverScale : 1;

	const tile = resolveTileRoot(cellRoot);
	if (tile) {
		tile.dataset.hovered = isHoverScaled ? "true" : "false";
		tile.dataset.trail = isPathTrail ? "true" : "false";
	}

	const pathTile = resolvePathTileRoot(cellRoot);
	syncPathTileHoverDataset(pathTile, isHoverScaled, isPathTrail);
	if (pathTile) {
		pathTile.dataset.active =
			isHoverScaled || isCharacterScaled ? "true" : "false";
	}

	cellRoot.style.setProperty("--hex-cell-hover-scale", String(scale));
	cellRoot.dataset.hoverScaled = isHoverScaled ? "true" : "false";
	cellRoot.dataset.characterScaled = isCharacterScaled ? "true" : "false";
	if (isHoverScaled || isCharacterScaled) {
		cellRoot.classList.add("z-20");
	} else {
		cellRoot.classList.remove("z-20");
	}
}

function syncPathTileHoverDataset(
	pathTile: HTMLElement | null,
	hovered: boolean,
	trail: boolean,
): void {
	if (!pathTile) {
		return;
	}
	pathTile.dataset.hovered = hovered ? "true" : "false";
	pathTile.dataset.trail = trail ? "true" : "false";
}

/** Path cell the character occupies. */
export function collectCharacterOccupiedCellKey(
	character: HexCoord | null,
): string | null {
	if (!character) {
		return null;
	}
	return coordKey(character.row, character.col);
}

/** Character tile plus neighbor trail cells (same set as hover DOM sync). */
export function collectCharacterTrailCellKeys(
	character: HexCoord | null,
): string[] {
	if (!character) {
		return [];
	}
	return collectPointerHoverAffectedCellKeys(character);
}

function collectAffectedCellKeys(
	hovered: HexCoord | null,
	characterKey: string | null,
): string[] {
	const keys = new Set<string>();
	for (const key of collectPointerHoverAffectedCellKeys(hovered)) {
		keys.add(key);
	}
	if (characterKey) {
		keys.add(characterKey);
	}
	return [...keys];
}

/** Hovered cell plus neighbor trail cells. */
export function collectPointerHoverAffectedCellKeys(
	pos: HexCoord | null,
): string[] {
	if (!pos) {
		return [];
	}
	const keys = [coordKey(pos.row, pos.col)];
	for (const neighbor of getNeighbors(pos.row, pos.col)) {
		keys.push(coordKey(neighbor.row, neighbor.col));
	}
	return keys;
}

/** Clears hover/trail datasets on a known set of cells (e.g. after forced dismiss). */
export function clearHexHoverDomState(
	affectedKeys: Iterable<string>,
	cellRoots: ReadonlyMap<string, HTMLElement>,
	options: HexGridHoverDomOptions,
): void {
	for (const key of affectedKeys) {
		const cellRoot = cellRoots.get(key);
		if (cellRoot) {
			syncHexCellVisuals(cellRoot, key, null, options);
		}
	}
}

export function syncHexCellHoverFromPointer(
	cellKey: string,
	cellRoot: HTMLElement,
	hovered: HexCoord | null,
	options: HexGridHoverDomOptions,
): void {
	syncHexCellVisuals(cellRoot, cellKey, hovered, options);
}

export function applyHexHoverDomTransition(
	prev: HexCoord | null,
	next: HexCoord | null,
	cellRoots: ReadonlyMap<string, HTMLElement>,
	options: HexGridHoverDomOptions,
): void {
	const keysToSync = new Set<string>([
		...collectAffectedCellKeys(prev, options.activeCharacterKey),
		...collectAffectedCellKeys(next, options.activeCharacterKey),
	]);

	for (const key of keysToSync) {
		const cellRoot = cellRoots.get(key);
		if (cellRoot) {
			syncHexCellVisuals(cellRoot, key, next, options);
		}
	}
}

export function applyHexCharacterActiveDomTransition(
	prevCharacter: HexCoord | null,
	nextCharacter: HexCoord | null,
	hovered: HexCoord | null,
	cellRoots: ReadonlyMap<string, HTMLElement>,
	options: HexGridHoverDomOptions,
): void {
	const prevKey = prevCharacter
		? coordKey(prevCharacter.row, prevCharacter.col)
		: null;
	const nextKey = nextCharacter
		? coordKey(nextCharacter.row, nextCharacter.col)
		: null;
	const keysToSync = new Set<string>([
		...collectAffectedCellKeys(hovered, prevKey),
		...collectAffectedCellKeys(hovered, nextKey),
	]);

	for (const key of keysToSync) {
		const cellRoot = cellRoots.get(key);
		if (cellRoot) {
			syncHexCellVisuals(cellRoot, key, hovered, options);
		}
	}
}
